import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  schema?: object;
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage,
  schema
}: SEOProps) {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://davidswebsitedesign.com';
  const fullUrl = `${siteUrl}${location.pathname}`;
  const canonicalUrl = canonical || fullUrl;

  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonicalUrl;

    // Update last-modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Open Graph tags
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:url', fullUrl, true);
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
    }

    // Twitter tags
    updateMetaTag('twitter:card', 'summary_large_image', true);
    updateMetaTag('twitter:url', fullUrl, true);
    updateMetaTag('twitter:title', title, true);
    updateMetaTag('twitter:description', description, true);
    if (ogImage) {
      updateMetaTag('twitter:image', ogImage, true);
    }

    // Schema.org JSON-LD
    if (schema) {
      let schemaScript = document.getElementById('schema-org') as HTMLScriptElement;
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.id = 'schema-org';
        schemaScript.type = 'application/ld+json';
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(schema);
    }
  }, [title, description, keywords, canonicalUrl, fullUrl, ogType, ogImage, schema]);
}

export function generateLocalBusinessSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://davidswebsitedesign.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "David's Website Design",
    "image": `${siteUrl}/logo.png`,
    "description": "Professional web design, SEO services, social media marketing, and turnkey websites in El Paso, TX. We create lead-generating websites with complete content creation.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "1000 Duskin Dr",
      "addressLocality": "El Paso",
      "addressRegion": "TX",
      "postalCode": "79907",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "31.761877",
      "longitude": "-106.485022"
    },
    "url": siteUrl,
    "telephone": "+1-915-XXX-XXXX",
    "priceRange": "$$",
    "areaServed": {
      "@type": "City",
      "name": "El Paso"
    },
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "31.761877",
        "longitude": "-106.485022"
      },
      "geoRadius": "50000"
    }
  };
}

export function generateServiceSchema(serviceName: string, serviceDescription: string) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://davidswebsitedesign.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": serviceName,
    "description": serviceDescription,
    "provider": {
      "@type": "ProfessionalService",
      "name": "David's Website Design",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "1000 Duskin Dr",
        "addressLocality": "El Paso",
        "addressRegion": "TX",
        "postalCode": "79907",
        "addressCountry": "US"
      }
    },
    "areaServed": {
      "@type": "City",
      "name": "El Paso"
    },
    "url": siteUrl
  };
}

export function generateWebPageSchema(name: string, description: string, url: string) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://davidswebsitedesign.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": name,
    "description": description,
    "url": url,
    "publisher": {
      "@type": "Organization",
      "name": "David's Website Design",
      "url": siteUrl
    }
  };
}
